import React from 'react';
import { FmChip } from '../FmChip';
import { FmHeading, FmText } from '../FmComposition';
import {
  FmKeyFigures,
  type FmKeyFigure as FmKeyFigureItem,
  type FmKeyFigureTone,
} from '../FmKeyFigures';
import { FmValueList } from '../FmValueList';
import { headingLabelText, plainHeadingText, useHostedHeading } from '../fmHostedHeading';
import type { FmStoryAccentTone, FmStoryBadge } from './elementFields.types';
import { useElementContext } from './elementContext';
import type {
  FmCopyElement,
  FmDefinitionListElement,
  FmKeyFigureStatus,
  FmFactsElement,
  FmKeyFiguresElement,
  FmStepsElement,
} from './elements.types';
import './FmElements.css';

/** Badge tones use the theme-aware chip fills so a badge reads on every band. */
const BADGE_VARIANT: Record<FmStoryAccentTone, 'success' | 'info' | 'warn' | 'mute'> = {
  accent: 'success',
  teal: 'success',
  amber: 'warn',
  sky: 'info',
  rose: 'warn',
  neutral: 'mute',
};

/** Only a declared status colours a figure: good, caution or a loss. */
const FIGURE_STATUS_TONE: Record<FmKeyFigureStatus, FmKeyFigureTone> = {
  good: 'positive',
  caution: 'warning',
  loss: 'danger',
};

const Badges: React.FC<{ badges: readonly FmStoryBadge[]; label: string | undefined }> = ({
  badges,
  label,
}) => (
  <div className="fm-element-copy__badges" aria-label={label}>
    {badges.map((badge) => (
      <FmChip
        key={`${badge.label}-${badge.tone ?? 'neutral'}`}
        variant={BADGE_VARIANT[badge.tone ?? 'neutral']}
      >
        {badge.label}
      </FmChip>
    ))}
  </div>
);

/**
 * A copy block below a band header (chapter, product chapter, region) is a
 * sub-section: one visual step smaller than its semantic level and a body-size
 * lede, so the band header keeps the only display-size title and lede.
 */
function isHostedCopy(headingLevel: number): boolean {
  return headingLevel > 2;
}

const CopyLede: React.FC<{ lede: string; hosted: boolean }> = ({ lede, hosted }) => (
  <p className={hosted ? 'fm-element-copy__lede fm-element-copy__lede--hosted' : 'fm-lede fm-element-copy__lede'}>
    {lede}
  </p>
);

const CopyList: React.FC<{ items: readonly string[] }> = ({ items }) => (
  <ul className="fm-element-copy__list">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

/** The eyebrow goes with its heading: a heading the host already printed takes it along. */
const CopyEyebrow: React.FC<{ element: FmCopyElement; showHeading: boolean }> = ({
  element,
  showHeading,
}) =>
  element.eyebrow && (showHeading || !element.heading) ? (
    <FmText size="fine" tone="accent" eyebrow>
      {element.eyebrow}
    </FmText>
  ) : null;

/**
 * Copy: the one place a section's eyebrow, heading and prose render. A heading
 * the hosting chapter already printed renders titleless, and its eyebrow goes
 * with it, so a section never says its chapter title twice.
 */
export const FmCopyElementView: React.FC<{ element: FmCopyElement }> = ({ element }) => {
  const { headingLevel, printedHeading } = useElementContext();
  const printedBySibling =
    printedHeading !== undefined &&
    plainHeadingText(element.heading) === plainHeadingText(printedHeading);
  const heading = useHostedHeading(element.heading, headingLevel, printedBySibling);
  const showHeading = Boolean(element.heading) && heading.render;
  const hosted = isHostedCopy(headingLevel);
  const visualLevel = Math.min(5, heading.level + (hosted ? 1 : 0)) as 2 | 3 | 4 | 5;
  return (
    <div
      className="fm-element-copy"
      data-scale={element.scale === 'display' ? 'display' : undefined}
      data-testid={element.testId}
    >
      <CopyEyebrow element={element} showHeading={showHeading} />
      {showHeading ? (
        <FmHeading level={Math.min(5, heading.level) as 2 | 3 | 4 | 5} visualLevel={visualLevel}>
          {element.heading}
        </FmHeading>
      ) : null}
      {element.lede ? <CopyLede lede={element.lede} hosted={hosted} /> : null}
      {element.badges?.length ? (
        <Badges badges={element.badges} label={headingLabelText(element.heading)} />
      ) : null}
      {element.body?.map((paragraph) => (
        <FmText key={paragraph}>{paragraph}</FmText>
      ))}
      {element.list?.length ? <CopyList items={element.list} /> : null}
      {element.sourceNote ? (
        <FmText size="caption" tone="muted">
          {element.sourceNote}
        </FmText>
      ) : null}
    </div>
  );
};

function toKeyFigureItem(
  figure: FmKeyFiguresElement['figures'][number],
  index: number,
): FmKeyFigureItem {
  const base = {
    id: `${index}-${figure.label}`,
    label: figure.label,
    unit: figure.unit,
    footnote: figure.footnote,
    ...(figure.status ? { tone: FIGURE_STATUS_TONE[figure.status] } : {}),
    ...(figure.delta ? { delta: figure.delta } : {}),
  };
  return figure.value === null
    ? { ...base, value: null, unavailableReason: figure.unavailableReason }
    : { ...base, value: figure.value };
}

/** Key figures as one unboxed rail; an unavailable figure keeps its label and names its reason. */
export const FmKeyFiguresElementView: React.FC<{ element: FmKeyFiguresElement }> = ({
  element,
}) => (
  <FmKeyFigures
    presentation="rail"
    layout="responsive"
    scale={element.scale ?? 'standard'}
    items={element.figures.map(toKeyFigureItem)}
    data-testid={element.testId}
  />
);

/**
 * Fact text stays neutral: green or teal text reads as a link and a muted value
 * reads as disabled, so only a loss (rose) colours a fact.
 */
function factValue(text: string, tone: FmStoryAccentTone | undefined): React.ReactNode {
  return tone === 'rose' ? <FmText tone="negative">{text}</FmText> : text;
}

/** Labelled facts: the value is optional context, the detail always explains it. */
export const FmFactsElementView: React.FC<{ element: FmFactsElement }> = ({ element }) => (
  <FmValueList
    density="compact"
    items={element.items.map((item, index) => ({
      id: `${index}-${item.label}`,
      label: item.label,
      value: factValue(item.value ?? item.detail, item.tone),
      helperText: item.value ? item.detail : undefined,
      prose: item.value === undefined,
    }))}
    data-testid={element.testId}
  />
);

/** A glossary: each term is the label, its definition reads as regular body text. */
export const FmDefinitionListElementView: React.FC<{ element: FmDefinitionListElement }> = ({
  element,
}) => (
  <dl className="fm-element-definitions" data-testid={element.testId}>
    {element.items.map((item) => (
      <div key={item.term} className="fm-element-definitions__item">
        <dt className="fm-element-definitions__term">{item.term}</dt>
        <dd className="fm-element-definitions__definition">{item.definition}</dd>
      </div>
    ))}
  </dl>
);

/** Reading steps: in-page links to the parts of the story, in order. */
export const FmStepsElementView: React.FC<{ element: FmStepsElement }> = ({ element }) => (
  <ol className="fm-element-steps" data-testid={element.testId}>
    {element.items.map((step) => (
      <li key={step.id}>
        <a className="fm-element-steps__link" href={`#${step.id}`}>
          <span>{step.label}</span>
          {step.meta ? <small>{step.meta}</small> : null}
        </a>
      </li>
    ))}
  </ol>
);
